/**
 * Turning a check-in into readable aspects.
 *
 * Three traps live here, which is why this is a tested module and not four
 * lines inside a component:
 *
 * 1. **The scales differ.** `mood` is 1..5, everything else is 1..10
 *    (`models.py::WellbeingEntry`). Four bars drawn against one maximum would
 *    show a perfect mood as 40% full.
 * 2. **Stress is inverted** — higher means worse. A shared "more is better"
 *    bar paints his calmest day red.
 * 3. **`sleep_quality` and `stress` are nullable** — they were not asked before
 *    2026-08-14. A missing answer is not a zero; a row with no answer is
 *    dropped rather than drawn at the bottom of the scale.
 */

import type { WellbeingEntry } from "../types";

/** The tones an aspect can carry — a subset of `StatusDot`'s vocabulary. */
export type AspectTone = "ok" | "warn" | "danger" | "muted";

export interface AspectRow {
  key: "sleep_quality" | "energy" | "stress" | "mood";
  label: string;
  /** The number as recorded, on this aspect's own scale. */
  value: number;
  max: number;
  /** 0..100 where 100 is always the good end — inverted for stress. */
  good_pct: number;
  tone: AspectTone;
  /** «8 / 10» — the figure the user reads, both halves on the same scale. */
  readout: string;
}

interface AspectSpec {
  key: AspectRow["key"];
  label: string;
  max: number;
  /** True when a high number is a bad thing. */
  inverted: boolean;
}

/**
 * Order is deliberate: sleep first because it is the one he controls the night
 * before, mood last because it is the softest signal.
 */
const ASPECTS: AspectSpec[] = [
  { key: "sleep_quality", label: "Сон", max: 10, inverted: false },
  { key: "energy", label: "Енергія", max: 10, inverted: false },
  { key: "stress", label: "Стрес", max: 10, inverted: true },
  { key: "mood", label: "Настрій", max: 5, inverted: false },
];

/**
 * Share of the scale in the *good* direction, 0..100.
 *
 * The bottom of every scale is 1, not 0 — a recorded 1 is the worst answer
 * available, so it reads as 0% rather than 10%.
 */
export function goodShare(value: number, max: number, inverted: boolean): number {
  const span = max - 1 || 1;
  const clamped = Math.min(Math.max(value, 1), max);
  const share = ((clamped - 1) / span) * 100;
  return inverted ? 100 - share : share;
}

/** Colour for a share of the good end. Two thresholds, stated once. */
export function aspectTone(goodPct: number): AspectTone {
  if (goodPct >= 66) return "ok";
  if (goodPct >= 33) return "warn";
  return "danger";
}

/**
 * The rows to draw for one check-in. Aspects he was never asked about are
 * absent from the result — not zeroed, not greyed.
 */
export function aspectRows(entry: WellbeingEntry | null | undefined): AspectRow[] {
  if (!entry) return [];
  const rows: AspectRow[] = [];
  for (const spec of ASPECTS) {
    const raw = entry[spec.key];
    if (raw === null || raw === undefined || !Number.isFinite(raw)) continue;
    const good = goodShare(raw, spec.max, spec.inverted);
    rows.push({
      key: spec.key,
      label: spec.label,
      value: raw,
      max: spec.max,
      good_pct: good,
      tone: aspectTone(good),
      readout: `${raw} / ${spec.max}`,
    });
  }
  return rows;
}

/**
 * Tone for the overall readiness figure. Same thresholds as an aspect, so the
 * headline and the rows under it cannot disagree about what "good" means.
 */
export function readinessTone(scorePct: number | null | undefined): AspectTone {
  if (scorePct === null || scorePct === undefined || !Number.isFinite(scorePct)) return "muted";
  return aspectTone(scorePct);
}
